import { defineStore } from "pinia";
import * as fb from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { RouteLocationNormalizedLoaded } from "vue-router";
import { ViewMode } from "@/utils/menuScope";

export interface MenuItem {
  Priority: number;
  Key: string;
  Label: string;
  MenuLabel?: string;
  Icon: string;
  Color: string;
  Route: string;
  Scope?: ViewMode | ReadonlyArray<ViewMode>;
  children?: MenuItem[];
}

interface MenuState {
  menus: MenuItem[];
  loading: boolean;
}

export const useMenuStore = defineStore("menu", {
  state: (): MenuState => ({
    menus: [],
    loading: false,
  }),

  actions: {
    async fetchMenus() {
      this.loading = true;
      try {
        const _menu = collection(fb.firestore, "menu");
        const items = await getDocs(_menu);

        if (items.size > 0) {
          let menu: MenuItem[] = [];
          items.forEach((d) => {
            const s = d.data() as MenuItem;
            menu.push(s);
          });

          // Sort by priority
          menu = menu.sort((a, b) =>
            a.Priority > b.Priority ? 1 : b.Priority > a.Priority ? -1 : 0
          );

          this.menus = menu;
        } else {
          console.warn("No configured menu could be retrieved");
          this.menus = [];
        }
      } catch (error) {
        console.error("Error fetching menu:", error);
        this.menus = [];
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },

  getters: {
    /**
     * Recursively search for a menu item by route name (Key field)
     */
    getMenuItemByRoute: (state) => {
      return (routeName: string): MenuItem | null => {
        const findMenu = (
          menus: MenuItem[],
          routeName: string
        ): MenuItem | null => {
          for (const menu of menus) {
            if (menu.Key === routeName) {
              return menu;
            }
            if (menu.children && menu.children.length > 0) {
              const child = findMenu(menu.children, routeName);
              if (child) {
                return child;
              }
            }
          }
          return null;
        };

        return findMenu(state.menus, routeName);
      };
    },

    /**
     * Extract fallback menu information from route meta
     */
    getFallbackMenu: () => {
      return (
        route: RouteLocationNormalizedLoaded
      ): { label: string; icon: string } | null => {
        if (route.meta.label || route.meta.icon) {
          return {
            label: (route.meta.label as string) || "Sin Título",
            icon: (route.meta.icon as string) || "fas fa-question",
          };
        }
        return null;
      };
    },

    /**
     * Get menu item for a route with fallback support
     * First tries to find in Firebase menu, then falls back to route meta
     */
    getMenuItemForRoute() {
      return (
        routeName: string | undefined,
        route: RouteLocationNormalizedLoaded
      ): { label: string; icon: string; color: string } => {
        // Try to get from Firebase menu
        if (routeName) {
          const menuItem = this.getMenuItemByRoute(routeName);
          if (menuItem) {
            return {
              label: menuItem.Label,
              icon: menuItem.Icon,
              color: menuItem.Color,
            };
          }
        }

        // Fallback to route meta
        const fallback = this.getFallbackMenu(route);
        if (fallback) {
          return { ...fallback, color: "white" };
        }

        // Ultimate fallback
        return {
          label: "Not Found",
          icon: "fas fa-bug",
          color: "cyan",
        };
      };
    },
  },
});
